import React, { useRef, useCallback, useEffect, useState } from "react";
import { useReducedMotion } from "@/components/cosmic/useReducedMotion";
import { scrollToElement } from "@/lib/scroll";
import { ArrowDown, FastForward, Compass } from "lucide-react";
const QUESTIONS = ["HOW DO YOU KNOW WHAT YOU KNOW?", "HOW DO YOU DISTINGUISH WHAT IS TRUE FROM WHAT MERELY FEELS TRUE?", "AND WHAT HAPPENS WHEN YOUR OWN MIND IS THE THING YOU MUST QUESTION?"] as const;
interface StarNode {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  connections: number[];
}
export function CinematicOpening() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const prefersReduced = useReducedMotion();

  // stage 0 -> 1 -> 2 -> 3 (all visible + CTA ready)
  const [stage, setStage] = useState<number>(() => prefersReduced ? 3 : 0);
  useEffect(() => {
    if (prefersReduced) {
      setStage(3);
      return;
    }
    const t1 = setTimeout(() => setStage(1), 800);
    const t2 = setTimeout(() => setStage(2), 3400);
    const t3 = setTimeout(() => setStage(3), 6200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [prefersReduced]);
  const handleSkipOrBegin = useCallback(() => {
    setStage(3);
    scrollToElement("knowledge-map", {
      block: "start",
      behavior: prefersReduced ? "auto" : "smooth"
    });
  }, [prefersReduced]);

  // Canvas constellation animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let animId: number;
    let width = canvas.width = canvas.offsetWidth;
    let height = canvas.height = canvas.offsetHeight;
    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = canvas.offsetWidth;
      height = canvas.height = canvas.offsetHeight;
    };
    window.addEventListener("resize", handleResize);
    const nodeCount = width < 768 ? 24 : 45;
    const nodes: StarNode[] = [];
    for (let i = 0; i < nodeCount; i++) {
      nodes.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
        r: 1.2 + Math.random() * 2,
        connections: []
      });
    }
    const mathSymbols = ["p ∧ q", "¬(p ∧ ¬p)", "∀x(Px → Qx)", "P(A|B)", "K = JTB?", "T ↔ p", "E ⊨ H"];
    const glyphs = Array.from({
      length: 8
    }, () => ({
      text: mathSymbols[Math.floor(Math.random() * mathSymbols.length)],
      x: Math.random() * width,
      y: Math.random() * height,
      alpha: 0.12 + Math.random() * 0.18
    }));
    let startTime = performance.now();
    const render = (time: number) => {
      const elapsed = (time - startTime) / 1000;
      ctx.clearRect(0, 0, width, height);

      // Deep space subtle gradient background
      const grad = ctx.createRadialGradient(width / 2, height / 2, 50, width / 2, height / 2, Math.max(width, height) / 1.5);
      grad.addColorStop(0, "rgba(5, 7, 20, 0.4)");
      grad.addColorStop(1, "rgba(2, 3, 8, 0.85)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);

      // Draw faint mathematical symbols
      ctx.font = "12px JetBrains Mono, monospace";
      for (const g of glyphs) {
        ctx.fillStyle = `rgba(0, 229, 255, ${g.alpha * 0.5})`;
        ctx.fillText(g.text, g.x, g.y);
      }

      // Draw and connect nodes
      const maxDist = width < 768 ? 95 : 130;
      for (let i = 0; i < nodes.length; i++) {
        const n1 = nodes[i];
        if (!prefersReduced) {
          n1.x += n1.vx;
          n1.y += n1.vy;
          if (n1.x < 0 || n1.x > width) n1.vx *= -1;
          if (n1.y < 0 || n1.y > height) n1.vy *= -1;
        }

        // Draw connections
        for (let j = i + 1; j < nodes.length; j++) {
          const n2 = nodes[j];
          const dx = n1.x - n2.x;
          const dy = n1.y - n2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < maxDist) {
            const alpha = (1 - dist / maxDist) * 0.25;
            ctx.strokeStyle = `rgba(0, 229, 255, ${alpha})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(n1.x, n1.y);
            ctx.lineTo(n2.x, n2.y);
            ctx.stroke();
          }
        }

        // Draw point
        ctx.fillStyle = i % 3 === 0 ? "rgba(0, 229, 255, 0.8)" : "rgba(167, 139, 250, 0.75)";
        ctx.beginPath();
        ctx.arc(n1.x, n1.y, n1.r, 0, Math.PI * 2);
        ctx.fill();
      }
      if (!prefersReduced) {
        animId = requestAnimationFrame(render);
      }
    };
    if (prefersReduced) {
      render(0);
    } else {
      animId = requestAnimationFrame(render);
    }
    return () => {
      window.removeEventListener("resize", handleResize);
      if (animId) cancelAnimationFrame(animId);
    };
  }, [prefersReduced]);
  return <section aria-label="The Architecture of Knowledge Introduction" className="relative w-full min-h-[92svh] flex flex-col justify-between items-center text-center px-4 py-8 select-none overflow-hidden">
      <canvas ref={canvasRef} aria-hidden="true" className="aok-opening-canvas" />

      {/* Top bar with quick skip button accessible immediately */}
      <div className="relative z-10 w-full max-w-6xl mx-auto flex items-center justify-between text-xs font-mono text-muted-foreground pt-2">
        <div className="flex items-center gap-2">
          <span className="inline-block uppercase tracking-[0.2em] text-primary font-bold text-[0.7rem]">
            PHILOSOPHICAL &amp; EPISTEMIC CARTOGRAPHY
          </span>
          <span className="hidden sm:inline text-border">/</span>
          <span className="hidden sm:inline text-muted-foreground text-[0.7rem]">
            15 SYSTEMIC DOMAINS
          </span>
        </div>

        <button type="button" onClick={handleSkipOrBegin} aria-label="Skip introductory sequence and jump to Knowledge Map" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-card/70 backdrop-blur-md text-foreground hover:border-primary hover:text-primary transition-colors text-xs min-h-[44px] focus-visible:ring-2 focus-visible:ring-ring">
          <span>Skip to Map</span>
          <FastForward className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
      </div>

      {/* Center cinematic text progression */}
      <div className="relative z-10 w-full max-w-4xl mx-auto my-auto flex flex-col items-center justify-center py-6 px-2">
        {/* Subtle eyebrow */}
        <div className="mb-4">
          <span className="ql-eyebrow">FIRST PRINCIPLES // REASON // PROOF</span>
        </div>

        {/* 3 Sequential Questions */}
        <div className="min-h-[14rem] sm:min-h-[12rem] flex flex-col items-center justify-center gap-4 my-4 w-full" aria-live="polite">
          {/* Question 1 */}
          <p className={`font-serif text-lg sm:text-2xl md:text-3xl font-bold tracking-wide text-foreground transition-all duration-700 max-w-3xl ${stage >= 1 || prefersReduced ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}>
            {QUESTIONS[0]}
          </p>

          {/* Question 2 */}
          <p className={`font-serif text-lg sm:text-2xl md:text-3xl font-bold tracking-wide text-primary transition-all duration-700 max-w-3xl ${stage >= 2 || prefersReduced ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}>
            {QUESTIONS[1]}
          </p>

          {/* Question 3 */}
          <p className={`font-serif text-lg sm:text-2xl md:text-3xl font-bold tracking-wide text-slate-200 transition-all duration-700 max-w-3xl ${stage >= 3 || prefersReduced ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}>
            {QUESTIONS[2]}
          </p>
        </div>

        {/* Climax title & subtitle */}
        <div className={`transition-all duration-700 mt-2 flex flex-col items-center ${stage >= 3 || prefersReduced ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}>
          <h1 className="font-serif text-3xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-b from-white via-slate-100 to-cyan-200 uppercase mb-3">
            THE ARCHITECTURE OF KNOWLEDGE
          </h1>

          <p className="font-sans italic text-sm sm:text-lg text-slate-300 max-w-2xl leading-relaxed mb-4">
            Explore reality, reason, evidence, consciousness, and the remarkable limits of human understanding.
          </p>

          <p className="font-sans text-xs sm:text-sm text-primary tracking-wide uppercase font-semibold mb-6">
            Explore Reality. Examine Evidence. Understand How We Know.
          </p>

          {/* Buttons: Begin Exploring & Enter Knowledge World */}
          <div className="flex flex-wrap items-center justify-center gap-3.5">
            <button type="button" onClick={handleSkipOrBegin} className="ql-neon-button min-h-[44px] flex items-center gap-2 px-7 py-3 text-sm sm:text-base font-serif font-bold uppercase tracking-wider">
              <span>BEGIN EXPLORING</span>
              <ArrowDown className="w-4 h-4" aria-hidden="true" />
            </button>

            <button type="button" onClick={handleSkipOrBegin} className="ql-button-secondary min-h-[44px] flex items-center gap-2 px-6 py-3 text-sm sm:text-base font-serif font-semibold tracking-wider">
              <Compass className="w-4 h-4 text-primary" aria-hidden="true" />
              <span>ENTER THE KNOWLEDGE WORLD</span>
            </button>
          </div>
        </div>
      </div>

      {/* Footer descriptor bar */}
      <div className="relative z-10 w-full max-w-6xl mx-auto flex flex-wrap items-center justify-between text-[0.75rem] font-mono text-muted-foreground pt-4 border-t border-border/40">
        <p>15 INTERCONNECTED DOMAINS &bull; 6 PHASE ONE LABS OPEN</p>
        <p className="text-right">LOCAL &bull; NO TRACKING &bull; RIGOROUS CRITICAL INQUIRY</p>
      </div>
    </section>;
}